import React, { useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { trackEvent } from 'user-analytics';
import LoadingScreen from 'components/LoadingScreen';
import useUserPreferences from 'hooks/useUserPreferences';
import { THREE_DS_REDIRECTED } from 'constants/TrackEvents';
import { isPayPalV6Enabled } from 'utils/paypalV6Config';
import errorMonitoring from 'errorMonitoring/ErrorMonitoring';
import { trackPaymentRedirect } from 'metrics/user-analytics/errorTracker';

const MAX_3DS_REDIRECTS = 3;

const propTypes = {
  token: PropTypes.string.isRequired,
  paymentId: PropTypes.number.isRequired,
  type: PropTypes.string,
  using3dSecure: PropTypes.bool,
  paymentRedirectUrl: PropTypes.string,
  paymentStatus: PropTypes.string,
  isFetching: PropTypes.bool.isRequired,
  loaded: PropTypes.bool.isRequired,
  getPayment: PropTypes.func.isRequired,
  transitionTo: PropTypes.func.isRequired,
  setError: PropTypes.func.isRequired,
  pathToMobile: PropTypes.string,

  paymentEngine: PropTypes.string,
  hasPreSelectedSeats: PropTypes.bool,
};

const defaultProps = {
  type: '',
  using3dSecure: false,
  paymentRedirectUrl: '',
};

/**
 * PaymentProcessor component.
 *
 * @param {Object} props - The component props.
 * @param {Function} props.getPayment - The function to get payment.
 * @param {boolean} props.isFetching - Flag indicating if the payment is being fetched.
 * @param {boolean} props.loaded - Flag indicating if the payment is loaded.
 * @param {number} props.paymentId - The payment ID.
 * @param {string} props.paymentRedirectUrl - The payment redirect URL.
 * @param {string} props.paymentStatus - The payment status.
 * @param {string} props.token - The payment token.
 * @param {Function} props.transitionTo - The function to transition to a new route.
 * @param {Function} props.setError - The function to set an error.
 * @param {string} props.pathToMobile - The path to the mobile version.
 * @param {string} props.type - The payment type.
 * @param {string} props.using3dSecure - The payment used 3d secure.
 * @param {string} props.paymentEngine - The payment engine.
 * @param {boolean} props.hasPreSelectedSeats - Flag indicating if pre-selected seats are available.
 */
const PaymentProcessor = ({
  getPayment,
  isFetching,
  loaded,
  paymentId,
  paymentRedirectUrl,
  paymentStatus,
  token,
  transitionTo,
  type,
  using3dSecure,
  setError,
  pathToMobile,
  paymentEngine,
  hasPreSelectedSeats,
}) => {
  const { env } = useSelector((state) => state.whitelabelConfig);
  const { passengers } = useUserPreferences();
  const hasTracked3DSRedirect = useRef(false);

  const track3DSRedirect = useCallback(() => {
    if (hasTracked3DSRedirect.current) return;

    hasTracked3DSRedirect.current = true;
    trackEvent(THREE_DS_REDIRECTED, {
      paymentId,
      paymentEngine,
      paymentStatus,
      paymentType: type,
      redirectUrl: paymentRedirectUrl,
    });
  }, [paymentId, paymentEngine, paymentStatus, type, paymentRedirectUrl]);

  const cardRedirect = useCallback(() => {
    const purchaseUrl = `${window.location.protocol}//${env.frontline.host}/purchases/${token}`;
    const hasFrequentPassengers = Boolean(passengers.length);

    const redirectKey = `3ds_redirected_${paymentId}`;
    const shouldRedirectTo3DS =
      using3dSecure &&
      paymentEngine !== 'stripe' &&
      paymentStatus === 'pending' &&
      paymentRedirectUrl &&
      !sessionStorage.getItem(redirectKey);

    const redirectCountKey = `3ds_redirect_count_${paymentId}`;
    const redirectCount = Number(sessionStorage.getItem(redirectCountKey) || 0);

    if (shouldRedirectTo3DS) {
      const newCount = redirectCount + 1;
      sessionStorage.setItem(redirectCountKey, String(newCount));

      if (newCount >= MAX_3DS_REDIRECTS) {
        errorMonitoring.notify({
          error: new Error('3DS redirect loop detected'),
          info: {
            paymentId,
            paymentEngine,
            paymentStatus,
            paymentType: type,
            redirectCount: newCount,
          },
        });
      }

      track3DSRedirect();
      sessionStorage.setItem(redirectKey, 'true');
      window.location = paymentRedirectUrl;
    } else if (using3dSecure && paymentStatus === 'pending') {
      // User returned from 3DS but webhook hasn't arrived yet.
      // Send to complete page where polling will wait for the final status.
      transitionTo(`/purchase/${token}/complete`);
    } else {
      switch (paymentStatus) {
        case 'attempt':
          if (paymentEngine === 'evertec') {
            transitionTo(`/purchase/${token}/complete`);
          }
          break;
        case 'charged':
          transitionTo(
            `/purchase/${token}/complete?trackPurchase=true&seatsOnResults=${Boolean(
              hasPreSelectedSeats,
            )}&frequentPassengers=${hasFrequentPassengers}`,
          );
          break;
        case 'suspicious':
          window.location = `${purchaseUrl}/pending`;
          break;
        case 'failed':
          if (paymentEngine === 'evertec') {
            transitionTo(`/payment/${token}/canceled`);
          } else {
            transitionTo(`/payment/${token}/${paymentId}/failure`);
          }
          break;
        case 'rejected':
          transitionTo(`/payment/${token}/${paymentId}/failure`);
          break;
        case 'chargeback':
          setError(301, 'error_when_generating_purchase', 'error', true);
          break;
        default:
          transitionTo(`${pathToMobile}/purchase/${token}/checkout`);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    paymentId,
    paymentRedirectUrl,
    paymentStatus,
    token,
    transitionTo,
    using3dSecure,
    passengers,
    track3DSRedirect,
  ]);

  const redirectToRoute = useCallback(() => {
    switch (type) {
      case 'paypal':
        // If PayPal V6 is enabled, don't redirect to PayPal site
        // The flow is handled entirely in the same window/modal
        if (isPayPalV6Enabled()) {
          // For PayPal V6, go directly to the purchase complete page
          // The SDK already handled the payment approval (modal/popup)
          // or will be auto-confirmed in PurchaseComplete (redirect fallback)
          transitionTo(`/purchase/${token}/complete?trackPurchase=true`);
        } else {
          // PayPal legacy: redirect to PayPal site
          trackPaymentRedirect({ provider: 'paypal', isExpected: true });
          window.location = paymentRedirectUrl;
        }
        break;

      case 'third_party':
        trackPaymentRedirect({ provider: 'third_party', isExpected: true });
        window.location = paymentRedirectUrl;
        break;

      case 'store':
        transitionTo(`/purchase/${token}/complete`);
        break;

      case 'credit_card':
      case 'reservamos_pay':
      case 'free_pay':
      case 'apple_pay':
      case 'google_pay':
        cardRedirect();
        break;

      case 'exchange':
        cardRedirect();
        break;

      default:
        transitionTo(`${pathToMobile}/purchase/${token}/checkout`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, paymentRedirectUrl, transitionTo, token, cardRedirect]);

  useEffect(() => {
    if (!loaded && !isFetching) {
      getPayment();
    } else {
      redirectToRoute();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    redirectToRoute();
  }, [isFetching, redirectToRoute]);

  if (!loaded || paymentStatus === 'attempt' || paymentStatus === 'chargeback')
    return <LoadingScreen />;

  if (type === 'paypal') return <LoadingScreen loadingText="redirecting_to_paypal" />;

  if (type === 'third_party') return <LoadingScreen loadingText="redirecting_to_coppel_pay" />;

  return <LoadingScreen />;
};

PaymentProcessor.propTypes = propTypes;
PaymentProcessor.defaultProps = defaultProps;

export default PaymentProcessor;
